import React from 'react';
import DocumentSearchResults from './DocumentSearchResults';
import TaskDocuments from './TaskDocuments';

interface DocumentRendererProps {
  content: string;
  messageMetadata?: any;
  onAttachDocument?: (documentId: string) => void;
  onDetachDocument?: (documentId: string) => void;
  onViewDocument?: (url: string) => void;
  onSearchDocuments?: () => void;
}

// Parse structured document data from agent responses
const parseDocumentData = (content: string) => {
  // Look for structured document data patterns in the content
  const searchResultsMatch = content.match(/📄 \*\*Found (\d+) relevant document/);
  const taskDocumentsMatch = content.match(/📋 \*\*"([^"]+)"\*\*.*?📄 (\d+) document/);
  const noDocumentsMatch = content.match(/🔍 \*\*No documents found\*\*/);
  const noAttachmentsMatch = content.match(/📄 No documents attached yet/);

  // Try to extract JSON-like data from HTML comments or structured text
  const jsonMatch = content.match(/<!--DOCUMENT_DATA:(.+?)-->/);
  let documentData = null;

  if (jsonMatch) {
    try {
      documentData = JSON.parse(jsonMatch[1]);
    } catch (e) {
      console.warn('Failed to parse document data:', e);
    }
  }

  return {
    isSearchResults: !!searchResultsMatch || !!noDocumentsMatch,
    isTaskDocuments: !!taskDocumentsMatch || !!noAttachmentsMatch,
    searchResultCount: searchResultsMatch ? parseInt(searchResultsMatch[1]) : 0,
    taskTitle: taskDocumentsMatch ? taskDocumentsMatch[1] : null,
    taskDocumentCount: taskDocumentsMatch ? parseInt(taskDocumentsMatch[2]) : 0,
    hasNoResults: !!noDocumentsMatch,
    hasNoAttachments: !!noAttachmentsMatch,
    documentData,
  };
};

// Extract document information from formatted text
const extractDocumentsFromText = (content: string) => {
  const documents = [];

  // Pattern to match document entries in the formatted text
  const documentPattern = /📕|📄|📊|📧 \*\*([^*]+)\*\*\s*\n\s*([^•]+)•([^•\n]+)/g;
  let match;

  while ((match = documentPattern.exec(content)) !== null) {
    const [, title, relevanceAndSource] = match;

    // Parse relevance and source
    const relevanceMatch = relevanceAndSource.match(/(High|Medium|Low|Very Low|\d+%) relevance/);
    const sourceMatch = relevanceAndSource.match(/•\s*([^•\n]+)$/);

    documents.push({
      document_node_id: `extracted_${documents.length}`,
      title: title.trim(),
      relevance_score: relevanceMatch ? (
        relevanceMatch[1].includes('%')
          ? parseInt(relevanceMatch[1]) / 100
          : { 'High': 0.8, 'Medium': 0.6, 'Low': 0.4, 'Very Low': 0.2 }[relevanceMatch[1]] || 0.5
      ) : 0.5,
      document_source: sourceMatch ? sourceMatch[1].trim() : 'Unknown',
      document_type: 'Document',
    });
  }

  return documents;
};

const DocumentRenderer: React.FC<DocumentRendererProps> = ({
  content,
  messageMetadata,
  onAttachDocument,
  onDetachDocument,
  onViewDocument,
  onSearchDocuments,
}) => {
  const parsedData = parseDocumentData(content);

  // If we have structured document data from metadata, use that
  if (messageMetadata?.documentSearchResults) {
    return (
      <DocumentSearchResults
        taskTitle={messageMetadata.taskTitle}
        searchQuery={messageMetadata.searchQuery}
        results={messageMetadata.documentSearchResults}
        totalCount={messageMetadata.totalCount || messageMetadata.documentSearchResults.length}
        onAttachDocument={onAttachDocument}
        onViewDocument={onViewDocument}
      />
    );
  }

  if (messageMetadata?.taskDocuments) {
    return (
      <TaskDocuments
        taskTitle={messageMetadata.taskTitle || 'Current Task'}
        attachments={messageMetadata.taskDocuments}
        totalCount={messageMetadata.totalCount || messageMetadata.taskDocuments.length}
        onDetachDocument={onDetachDocument}
        onViewDocument={onViewDocument}
        onSearchDocuments={onSearchDocuments}
      />
    );
  }

  // Fallback: try to parse document information from formatted text
  if (parsedData.isSearchResults) {
    const extractedDocuments = extractDocumentsFromText(content);

    if (extractedDocuments.length > 0 || parsedData.hasNoResults) {
      return (
        <DocumentSearchResults
          searchQuery="Extracted from message"
          results={extractedDocuments}
          totalCount={parsedData.searchResultCount || extractedDocuments.length}
          onAttachDocument={onAttachDocument}
          onViewDocument={onViewDocument}
        />
      );
    }
  }

  if (parsedData.isTaskDocuments) {
    const extractedDocuments = extractDocumentsFromText(content);

    // Convert to attachment format
    const attachments = extractedDocuments.map((doc, index) => ({
      id: `attachment_${index}`,
      document_node_id: doc.document_node_id,
      document_title: doc.title,
      document_type: doc.document_type,
      document_source: doc.document_source,
      relevance_score: doc.relevance_score,
      attachment_method: 'manual' as const,
      attached_at: new Date().toISOString(),
    }));

    return (
      <TaskDocuments
        taskTitle={parsedData.taskTitle || 'Current Task'}
        attachments={attachments}
        totalCount={parsedData.taskDocumentCount || attachments.length}
        onDetachDocument={onDetachDocument}
        onViewDocument={onViewDocument}
        onSearchDocuments={onSearchDocuments}
      />
    );
  }

  // If no document-specific rendering is needed, return null
  return null;
};

export default DocumentRenderer;